import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { extractErrorMessage } from '../../../utils/errorUtils';
import {
  useDeleteAiCredentialMutation,
  useSaveAiCredentialMutation,
} from '../api/aiImplementationApi';
import {
  DEFAULT_AI_PROVIDER,
  AI_PROVIDER_OPEN_AI,
  AI_PROVIDER_OPEN_AI_COMPATIBLE,
  AI_PROVIDER_ANTHROPIC,
  type AiCredential,
  type AiProvider,
  type AiReasoningEffort,
} from '../types';
import { useGitHubDeviceFlow } from '../hooks/useGitHubDeviceFlow';

const providers: { value: AiProvider; label: string }[] = [
  { value: AI_PROVIDER_OPEN_AI, label: 'OpenAI' },
  { value: AI_PROVIDER_OPEN_AI_COMPATIBLE, label: 'OpenAI-compatible' },
  { value: AI_PROVIDER_ANTHROPIC, label: 'Anthropic' },
];

const reasoningOptions: Record<AiProvider, { value: AiReasoningEffort; label: string }[]> = {
  OpenAI: [
    { value: 'none', label: 'None' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'xhigh', label: 'XHigh' },
  ],
  OpenAICompatible: [
    { value: 'default', label: 'Default' },
    { value: 'none', label: 'None' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'xhigh', label: 'XHigh' },
  ],
  Anthropic: [
    { value: 'default', label: 'Default' },
    { value: 'high', label: 'High' },
    { value: 'max', label: 'Max' },
  ],
};

const defaultReasoningEffort = (provider: AiProvider): AiReasoningEffort =>
  provider === AI_PROVIDER_OPEN_AI ? 'medium' : 'default';

const normalizeReasoningEffort = (
  provider: AiProvider,
  effort?: string | null
): AiReasoningEffort =>
  reasoningOptions[provider].some((item) => item.value === effort)
    ? (effort as AiReasoningEffort)
    : defaultReasoningEffort(provider);

type Props = {
  credentials: AiCredential[];
  onClose: () => void;
  refetchCredentials: () => void;
};

export const ConfigureAiProviderForm = ({ credentials, onClose, refetchCredentials }: Props) => {
  const [saveCredential, saveState] = useSaveAiCredentialMutation();
  const [deleteCredential, deleteState] = useDeleteAiCredentialMutation();

  const [selectedId, setSelectedId] = useState<number | 'new'>('new');
  const [provider, setProvider] = useState<AiProvider>(DEFAULT_AI_PROVIDER);
  const [modelId, setModelId] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [gitHubOwner, setGitHubOwner] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [reasoningEffort, setReasoningEffort] = useState<AiReasoningEffort>('medium');
  const [isDefault, setIsDefault] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const gitHubFlow = useGitHubDeviceFlow({ onSuccess: refetchCredentials });

  const hasGitHubPat = useMemo(() => {
    return credentials.some((c) => c.hasGitHubPat);
  }, [credentials]);
  const gitHubPatLastFour = useMemo(() => {
    return credentials.find((c) => c.hasGitHubPat)?.gitHubPatLastFour;
  }, [credentials]);

  const selectedCredential =
    selectedId === 'new' ? undefined : credentials.find((item) => item.id === selectedId);
  const busy = saveState.isLoading || deleteState.isLoading;

  const applyCredential = (credential?: AiCredential) => {
    const credentialProvider = credential?.provider ?? DEFAULT_AI_PROVIDER;
    setSelectedId(credential?.id ?? 'new');
    setProvider(credentialProvider);
    setModelId(credential?.modelId ?? '');
    setApiBaseUrl(credential?.apiBaseUrl ?? '');
    setApiKey('');
    setGitHubOwner(credential?.gitHubOwner ?? '');
    setAdditionalInstructions(credential?.additionalInstructions ?? '');
    setReasoningEffort(normalizeReasoningEffort(credentialProvider, credential?.reasoningEffort));
    setIsDefault(credential?.isDefault ?? credentials.length === 0);
    setError(null);
    setSuccess(null);
    gitHubFlow.cancelFlow();
    gitHubFlow.clearMessages();
  };

  useEffect(() => {
    if (selectedId !== 'new' && credentials.some((item) => item.id === selectedId)) return;
    applyCredential(credentials.find((item) => item.isDefault) ?? credentials[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials]);

  const handleSave = async () => {
    const normalizedModel = modelId.trim();
    const normalizedApiBaseUrl = apiBaseUrl.trim();
    const normalizedKey = apiKey.trim();
    if (!normalizedModel) {
      setError('Model identifier is required.');
      return;
    }
    if (
      provider === AI_PROVIDER_OPEN_AI_COMPATIBLE &&
      !/^https:\/\/.+/i.test(normalizedApiBaseUrl)
    ) {
      setError('An absolute HTTPS API base URL is required for an OpenAI-compatible provider.');
      return;
    }
    if (!selectedCredential && !normalizedKey) {
      setError('API key is required for a new provider.');
      return;
    }

    setError(null);
    setSuccess(null);
    try {
      const saved = await saveCredential({
        id: selectedCredential?.id,
        provider,
        modelId: normalizedModel,
        apiBaseUrl: provider === AI_PROVIDER_OPEN_AI_COMPATIBLE ? normalizedApiBaseUrl : undefined,
        apiKey: normalizedKey || undefined,
        gitHubOwner: gitHubOwner.trim() || undefined,
        additionalInstructions: additionalInstructions.trim() || undefined,
        reasoningEffort,
        isDefault,
      }).unwrap();
      setSelectedId(saved.id);
      setApiKey('');
      setSuccess('AI provider settings were saved.');
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'Failed to save AI provider settings.'));
    }
  };

  const handleDelete = async () => {
    if (!selectedCredential) return;
    if (!window.confirm(`Delete ${selectedCredential.provider} credential?`)) return;

    setError(null);
    setSuccess(null);
    try {
      await deleteCredential(selectedCredential.id).unwrap();
      applyCredential();
      setSuccess('AI provider settings were deleted.');
    } catch (deleteError) {
      setError(extractErrorMessage(deleteError, 'Failed to delete AI provider settings.'));
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField
            select
            label="Configuration"
            value={selectedId}
            onChange={(event) => {
              const value = event.target.value;
              applyCredential(
                value === 'new' ? undefined : credentials.find((item) => item.id === Number(value))
              );
            }}
            disabled={busy}
          >
            <MenuItem value="new">Add new provider</MenuItem>
            {credentials.map((credential) => (
              <MenuItem key={credential.id} value={credential.id}>
                {credential.provider} — {credential.modelId}
                {credential.isDefault ? ' (default)' : ''}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            required
            label="Provider"
            value={provider}
            onChange={(event) => {
              const nextProvider = event.target.value as AiProvider;
              setProvider(nextProvider);
              setReasoningEffort(normalizeReasoningEffort(nextProvider, reasoningEffort));
            }}
            disabled={busy}
          >
            {providers.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            required
            label="Model identifier"
            value={modelId}
            onChange={(event) => setModelId(event.target.value)}
            placeholder="For example: gpt-5.1-codex"
            inputProps={{ maxLength: 200 }}
            disabled={busy}
          />

          <TextField
            select
            required
            label="Reasoning effort"
            value={reasoningEffort}
            onChange={(event) => setReasoningEffort(event.target.value as AiReasoningEffort)}
            helperText={
              provider === AI_PROVIDER_OPEN_AI_COMPATIBLE
                ? 'Support depends on the selected OpenAI-compatible provider and model.'
                : 'Controls the reasoning variant used by the coding agent.'
            }
            disabled={busy}
          >
            {reasoningOptions[provider].map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            multiline
            minRows={3}
            label="Additional instructions"
            value={additionalInstructions}
            onChange={(event) => setAdditionalInstructions(event.target.value)}
            inputProps={{ maxLength: 4000 }}
            helperText="Optional instructions added to the coding agent prompt for this configuration."
            disabled={busy}
          />

          {provider === AI_PROVIDER_OPEN_AI_COMPATIBLE && (
            <TextField
              required
              label="API base URL"
              value={apiBaseUrl}
              onChange={(event) => setApiBaseUrl(event.target.value)}
              placeholder="https://api.example.com/v1"
              inputProps={{ maxLength: 500 }}
              helperText="Use the provider's OpenAI-compatible API endpoint (for example, https://api.groq.com/openai/v1)."
              disabled={busy}
            />
          )}

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              GitHub Connection
            </Typography>

            {gitHubFlow.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {gitHubFlow.error}
              </Alert>
            )}
            {gitHubFlow.successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {gitHubFlow.successMessage}
              </Alert>
            )}

            {hasGitHubPat && gitHubFlow.deviceFlowStatus !== 'polling' ? (
              <Typography variant="body2" color="text.secondary">
                GitHub is connected. (PAT ends in {gitHubPatLastFour ?? '****'})
                <br />
                <Button
                  variant="text"
                  size="small"
                  onClick={gitHubFlow.startFlow}
                  disabled={gitHubFlow.isLoading}
                  sx={{ mt: 1, p: 0 }}
                >
                  Reconnect GitHub
                </Button>
              </Typography>
            ) : gitHubFlow.deviceFlowStatus === 'polling' && gitHubFlow.deviceFlow ? (
              <Stack spacing={2}>
                <Alert severity="info" icon={<CircularProgress size={20} />}>
                  <Typography variant="body2">
                    1. Go to{' '}
                    <a
                      href={gitHubFlow.deviceFlow.verification_uri}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {gitHubFlow.deviceFlow.verification_uri}
                    </a>
                  </Typography>
                  <Typography variant="body2">
                    2. Enter code: <strong>{gitHubFlow.deviceFlow.user_code}</strong>
                  </Typography>
                  <Typography variant="body2" mt={1}>
                    Waiting for authorization...
                  </Typography>
                </Alert>
                <Button size="small" onClick={gitHubFlow.cancelFlow}>
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="body2" color="text.secondary">
                  Required for the AI worker to interact with repositories. This uses GitHub's
                  device authorization flow.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={gitHubFlow.startFlow}
                  disabled={gitHubFlow.isLoading}
                >
                  {gitHubFlow.isLoading ? 'Connecting...' : 'Connect GitHub'}
                </Button>
              </Stack>
            )}
          </Box>

          <TextField
            required={!selectedCredential}
            type="password"
            label="API key"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            autoComplete="new-password"
            inputProps={{ maxLength: 4000 }}
            helperText={
              selectedCredential?.hasApiKey
                ? `A key ending in ${selectedCredential.keyLastFour ?? '****'} is stored. Leave this field empty to keep it.`
                : 'The key is encrypted and is never returned to the browser.'
            }
            disabled={busy}
          />

          <TextField
            label="GitHub Owner (optional)"
            value={gitHubOwner}
            onChange={(event) => setGitHubOwner(event.target.value)}
            placeholder="e.g. OSKI-solutions"
            inputProps={{ maxLength: 100 }}
            helperText="Only set this if in database is wrong naming of repo owner"
            disabled={busy}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isDefault}
                onChange={(event) => setIsDefault(event.target.checked)}
                disabled={busy}
              />
            }
            label="Use as default configuration"
          />

          <Typography variant="body2" color="text.secondary">
            Provider credits may be consumed only after you confirm an Implement with AI run.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        {selectedCredential && (
          <Button color="error" onClick={handleDelete} disabled={busy}>
            Delete
          </Button>
        )}
        <Box flex={1} />
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={busy}>
          {saveState.isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </>
  );
};
